/**
 * THE REEL'S FIGURES — every number the capability reel shows, as the words it shows.
 *
 * The browser renders and never derives (tests/integration/src/workos/browserLaw.test.ts), and that holds for
 * a film of sample work too: nothing here adds, divides or formats. Each figure is written once as the text the
 * reel shows, beside its amount in minor units, and figures.test.ts does the arithmetic an accountant watching
 * would do (running balances, the reconciliation, the profit) and proves every text agrees with it.
 *
 * The clients are invented, and so are their figures.
 */

export interface Amount { minor: number; text: string }
export interface StatementLine { date: string; details: string; minor: number; amount: string; balance: string }

/** Acme Trading Ltd, GBP. The reel shows the first lines of page 1 of the first statement being read, in the bank's own narrations. */
export const ACME = {
  client: "Acme Trading Ltd",
  period: "September 2026",
  currency: "GBP",
  firstStatement: "1–15 Sep",
  secondStatement: "20–30 Sep",
  gap: "16–19 Sep",
  statementLines: 211,
  matched: 209,
  gapCashBookLines: 2,
  opening: { minor: 1_824_055, text: "18,240.55" } as Amount,
  lines: [
    { date: "01 Sep", details: "CARD OFFICE SUPPLY", minor: -8_640, amount: "−86.40", balance: "18,154.15" },
    { date: "02 Sep", details: "TFR NORTHWIND FOODS", minor: 420_000, amount: "+4,200.00", balance: "22,354.15" },
    { date: "02 Sep", details: "DD CITY POWER", minor: -31_218, amount: "−312.18", balance: "22,041.97" },
    { date: "03 Sep", details: "CHQ 104512", minor: -115_000, amount: "−1,150.00", balance: "20,891.97" },
    { date: "03 Sep", details: "CARD FUEL STATION", minor: -6_420, amount: "−64.20", balance: "20,827.77" },
    { date: "04 Sep", details: "TFR HARBOR FREIGHT", minor: -248_000, amount: "−2,480.00", balance: "18,347.77" },
    { date: "04 Sep", details: "CARD STATIONERY HSE", minor: -2_375, amount: "−23.75", balance: "18,324.02" },
  ] as StatementLine[],
  /** Balance per the second statement at 30 September. */
  statementClosing: { minor: 2_190_610, text: "21,906.10" } as Amount,
  /** Balance per the cash book at 30 September: what the named items say it must be. */
  cashBook: { minor: 2_548_246, text: "25,482.46" } as Amount,
  difference: { minor: 357_636, text: "3,576.36" } as Amount,
  unpresentedCheque: { ref: "Cheque 104530", minor: 64_000, text: "640.00" },
  bankCharge: { minor: 1_850, text: "18.50" } as Amount,
  interest: { minor: 214, text: "2.14" } as Amount,
  duplicateReceipt: { from: "Northwind Foods", minor: 420_000, text: "4,200.00" },
} as const;

export interface Pattern { key: string; pattern: string; lines: number; minor: number; amount: string; account: string; how: "RULE" | "SUGGESTED" | "INVOICES" | "ASKED"; side: "IN" | "OUT" }
export interface ScheduleRow { label: string; minor: number; text: string; kind: "plain" | "total" | "rule" }

/** Harbour Kitchen Ltd, NGN, year to 31 March 2026: the bank lines by pattern, and the draft profit they make. */
export const HARBOUR = {
  client: "Harbour Kitchen Ltd",
  period: "Year to 31 March 2026",
  currency: "NGN",
  lineCount: 585,
  patterns: [
    { key: "pos", pattern: "POS settlement · CARDPOINT", lines: 412, minor: 4_821_530_000, amount: "48,215,300.00", account: "Sales", how: "RULE", side: "IN" },
    { key: "farm", pattern: "Transfer out · FRESH FARMS SUPPLY", lines: 96, minor: 1_988_000_000, amount: "19,880,000.00", account: "Cost of sales", how: "SUGGESTED", side: "OUT" },
    { key: "rent", pattern: "Transfer out · PRIME PROPERTIES", lines: 4, minor: 600_000_000, amount: "6,000,000.00", account: "Rent", how: "INVOICES", side: "OUT" },
    { key: "power", pattern: "Direct debit · CITY POWER", lines: 12, minor: 241_050_000, amount: "2,410,500.00", account: "Utilities", how: "SUGGESTED", side: "OUT" },
    { key: "fuel", pattern: "Card payment · fuel", lines: 58, minor: 196_420_000, amount: "1,964,200.00", account: "Motor expenses", how: "RULE", side: "OUT" },
    { key: "unknown", pattern: "Transfer out · no name given", lines: 3, minor: 125_000_000, amount: "1,250,000.00", account: "Suspense", how: "ASKED", side: "OUT" },
  ] as Pattern[],
  /** The draft profit and loss as a schedule shows it: costs in brackets. Suspense is held aside, never in the profit. */
  profitAndLoss: [
    { label: "Revenue", minor: 4_821_530_000, text: "48,215,300.00", kind: "plain" },
    { label: "Cost of sales", minor: -1_988_000_000, text: "(19,880,000.00)", kind: "plain" },
    { label: "Gross profit", minor: 2_833_530_000, text: "28,335,300.00", kind: "total" },
    { label: "Rent", minor: -600_000_000, text: "(6,000,000.00)", kind: "plain" },
    { label: "Utilities", minor: -241_050_000, text: "(2,410,500.00)", kind: "plain" },
    { label: "Motor expenses", minor: -196_420_000, text: "(1,964,200.00)", kind: "plain" },
    { label: "Net profit", minor: 1_796_060_000, text: "17,960,600.00", kind: "rule" },
  ] as ScheduleRow[],
  suspense: { minor: 125_000_000, text: "1,250,000.00" } as Amount,
} as const;
